import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Heart } from "lucide-react";
import { api } from "@/lib/api";
import { RevealLines, FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import Seo from "@/components/Seo";

const SponsorMark = ({ s, size = "roster" }) => {
  // Logo sits on a chip (white by default, or black for light/white logos via dark_chip toggle).
  if (s.logo) {
    const chipBg = s.dark_chip ? "bg-ink" : "bg-white";
    const box =
      size === "marquee"
        ? `${chipBg} w-full h-full grid place-items-center p-4`
        : `${chipBg} w-full h-28 grid place-items-center p-4`;
    return (
      <div className={box}>
        <img src={s.logo} alt={s.name} className="w-full h-full object-contain" />
      </div>
    );
  }
  const box =
    size === "marquee"
      ? "w-full h-full grid place-items-center px-4"
      : "w-full h-28 grid place-items-center px-4";
  return (
    <div className={box}>
      <span className="font-display text-xl lg:text-2xl font-bold tracking-tight text-white text-center leading-tight">
        {s.name}
      </span>
    </div>
  );
};

export default function Sponsors() {
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    api.get("/sponsors").then(({ data }) => setSponsors(data)).catch(() => {});
  }, []);

  const featured = sponsors.filter((s) => !s.compact);
  const compact = sponsors.filter((s) => s.compact);

  return (
    <div data-testid="sponsors-page" className="bg-ink text-white">
      <Seo
        title="Thank You to Our Sponsors"
        description="Sponsors and partners make community-centered journalism possible. Meet the organizations powering The Canary Report."
      />

      {/* HERO */}
      <section className="relative grain overflow-hidden pt-[120px] pb-16 lg:pb-20">
        <div className="relative z-10 max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12">
          <p className="label text-canary mb-6 flex items-center gap-3">
            <Heart size={15} className="fill-canary text-canary" /> With gratitude
          </p>
          <RevealLines
            as="h1"
            className="font-display font-black text-[13vw] lg:text-[7.2vw] leading-[0.86] tracking-tight"
            lines={["Thank you to", "our sponsors."]}
          />
          <FadeUp delay={0.6}>
            <p className="mt-8 max-w-2xl text-lg text-white/70 leading-relaxed">
              Independent, community-centered journalism is built by the people and organizations
              who believe in it. These partners make our reporting, our photography, and our
              Lift Every Voice convenings possible.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* SCROLLING MARQUEE (left -> right) */}
      <section data-testid="sponsors-marquee" className="border-y border-white/15 bg-white/[0.03] py-14 lg:py-20">
        {featured.length === 0 ? (
          <p className="text-center label text-white/40">Our sponsor wall is coming soon.</p>
        ) : (
          <Marquee direction="right" speed={40} gradient={false} pauseOnHover autoFill>
            {featured.map((s) => (
              <a
                key={s.id}
                href={s.link || undefined}
                target={s.link ? "_blank" : undefined}
                rel={s.link ? "noopener noreferrer" : undefined}
                data-testid={`sponsor-marquee-${s.id}`}
                className={`group mx-6 lg:mx-10 flex h-28 w-[240px] lg:w-[280px] items-center justify-center border border-white/15 overflow-hidden transition-colors duration-300 ${s.link ? "hover:border-canary" : ""}`}
              >
                <SponsorMark s={s} size="marquee" />
              </a>
            ))}
          </Marquee>
        )}
      </section>

      {/* SPONSOR ROSTER (added value: tier + blurb + link) */}
      <section className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-24 lg:py-32">
        <FadeUp>
          <p className="label text-canary mb-4">The Honor Roll</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-[0.95]">
            Partners in the work.
          </h2>
        </FadeUp>

        <Stagger className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/15 border border-white/15">
          {featured.map((s) => (
            <StaggerItem key={s.id}>
              <div data-testid={`sponsor-card-${s.id}`} className="bg-ink h-full min-h-[280px] flex flex-col group hover:bg-white/[0.04] transition-colors duration-300 overflow-hidden">
                <SponsorMark s={s} size="roster" />
                <div className="p-8 pt-6 flex-1 flex flex-col justify-between">
                  <div>
                    <p className="label text-canary mb-2">{s.tier}</p>
                    {s.blurb && <p className="text-white/70 leading-relaxed">{s.blurb}</p>}
                  </div>
                  {s.link && (
                    <a
                      href={s.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`sponsor-link-${s.id}`}
                      className="inline-flex items-center gap-2 mt-4 label text-white/60 group-hover:text-white transition-colors"
                    >
                      Visit site <ArrowUpRight size={14} />
                    </a>
                  )}
                </div>
              </div>
            </StaggerItem>
          ))}
          {featured.length === 0 && (
            <p className="bg-ink p-8 text-white/50">Sponsors will appear here soon.</p>
          )}
        </Stagger>
      </section>

      {/* COMMUNITY PARTNERS (compact, name-only — smaller donors) */}
      {compact.length > 0 && (
        <section data-testid="community-partners" className="border-t border-white/10 bg-white/[0.02]">
          <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24">
            <FadeUp>
              <p className="label text-canary mb-4">Community Partners</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-3xl leading-[0.98]">
                And, with gratitude, from every corner of the community.
              </h2>
              <p className="mt-5 text-white/60 max-w-2xl">
                Individuals and organizations whose support &mdash; at every level &mdash; makes this work possible.
              </p>
            </FadeUp>
            <Stagger className="mt-10 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-4">
              {compact.map((s) => (
                <StaggerItem key={s.id}>
                  {s.link ? (
                    <a
                      href={s.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`community-partner-${s.id}`}
                      className="font-display text-lg lg:text-xl font-bold text-white/85 hover:text-canary transition-colors block border-b border-white/10 pb-3"
                    >
                      {s.name}
                    </a>
                  ) : (
                    <p
                      data-testid={`community-partner-${s.id}`}
                      className="font-display text-lg lg:text-xl font-bold text-white/85 border-b border-white/10 pb-3"
                    >
                      {s.name}
                    </p>
                  )}
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-canary text-ink">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-20 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <FadeUp>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-2xl leading-[1.02]">
              Join the organizations powering this work.
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <Link
              to="/partner"
              data-testid="sponsors-become-btn"
              className="inline-flex items-center gap-2 label bg-ink text-white px-9 py-5 rounded-full hover:bg-white hover:text-ink transition-colors duration-300 whitespace-nowrap"
            >
              Become a sponsor <ArrowUpRight size={16} />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
